import React, { Component } from 'react'
import { RadioGroup, Radio } from '../../../libraries/react-radio-group'
import { DatePicker } from '@tecuity/granite-ui/'
import { isValid, format } from 'date-fns'
import { DumbField } from '../Field'
import FieldTooltip from '../Tooltip'
import { getValidityClass } from '../Field'
import Portal from 'components/Portal/Portal'
import shortid from 'shortid'
import CalendarIcon from '@tecuity/sos-icons/react/calendar'
import ReactMarkdown from 'components/Markdown/Markdown'

const DEFAULT_INPUT_MAX_LENGTH = process.env.DEFAULT_INPUT_MAX_LENGTH

export class TextInput extends Component {
  constructor(props) {
    super(props)
    this.state = { selectionStart: null }
  }
  static defaultProps = {
    mask: [],
    onBlur: () => { },
  }
  preserveCursor() {
    this.setState({ selectionStart: this.input.selectionStart })
  }
  setCursor() {
    this.input.setSelectionRange(this.state.selectionStart, this.state.selectionStart)
  }
  componentDidUpdate(prevProps) {
    if (
      (!this.props.mask.length || this.props.mask[0].fn === 'allUpperCase') &&
      this.props.value !== prevProps.value &&
      !this.props.disableSelectionRestoration
    ) {
      this.setCursor()
    }
  }
  hasCharacterLimitMask = () => {
    return (
      Array.isArray(this.props.mask) &&
      !!this.props.mask.find((mask) => mask.fn === 'maxStringLength')
    )
  }
  setFieldValue = (e) => {
    let value = e.target.value
    if (!this.hasCharacterLimitMask()) {
      value = value.slice(0, DEFAULT_INPUT_MAX_LENGTH || 256)
    }
    this.props.onChange(value, e)
    this.preserveCursor()
  }
  triggerBlur = (e) => {
    if (this.props.onBlur) {
      let value = e.target.value
      if (!this.hasCharacterLimitMask()) {
        value = value.slice(0, DEFAULT_INPUT_MAX_LENGTH || 256)
      }
      this.props.onBlur(value, e)
    }
  }
  onKeyPress = (e) => {
    if (this.props.onKeyPress) {
      this.props.onKeyPress(e)
    }
  }
  focus = () => {
    this.input.focus()
  }
  blur = () => {
    this.input.blur()
  }
  render() {
    let {
      onKeyPress,
      onFocus,
      className = '',
      validityClass = '',
      width,
      name,
      autoComplete,
      disabled,
      index,
      value,
      placeholder,
      maxLength,
      textAlignment = 'left',
      passwordMask,
      onClick,
      required,
      fieldId = '',
      ariaLabel,
      tabIndex,
      isValid,
      style = {},
    } = this.props
    const inputType = passwordMask ? 'password' : 'text'
    return (
      <div className={'text-wrapper ' + className + ' ' + validityClass} style={{ width: width }}>
        <input
          type={inputType}
          onKeyPress={onKeyPress}
          onChange={this.setFieldValue}
          onClick={onClick ? onClick : null}
          onBlur={this.triggerBlur}
          onFocus={onFocus}
          name={name}
          autoComplete={autoComplete}
          disabled={disabled}
          data-index={index}
          value={value ? value : ''}
          placeholder={placeholder}
          aria-required={required}
          required={required}
          aria-label={ariaLabel}
          aria-describedby={
            fieldId && this.props.description
              ? `field-description-${fieldId} field-error-${fieldId}`
              : null
          }
          aria-invalid={isValid === false}
          id={fieldId ? `field-${fieldId}` : undefined}
          maxLength={maxLength}
          style={{ textAlign: textAlignment, ...style }}
          tabIndex={tabIndex}
          ref={(input) => {
            this.input = input
          }}
        />
      </div>
    )
  }
}

export class TextArea extends Component {
  static defaultProps = {
    mask: [],
  }
  hasCharacterLimitMask = () => {
    return !!this.props.mask.find((mask) => mask.fn === 'maxStringLength')
  }
  setFieldValue = (e) => {
    let value = e.target.value
    if (!this.hasCharacterLimitMask()) {
      value = value.slice(0, 1000)
    }
    this.props.onChange(value, e)
  }
  triggerBlur = (e) => {
    if (this.props.onBlur) {
      let value = e.target.value
      if (!this.hasCharacterLimitMask()) {
        value = value.slice(0, 1000)
      }
      this.props.onBlur(value, e)
    }
  }
  focus = () => {
    this.input.focus()
  }
  render() {
    let props = this.props
    return (
      <div
        className={'text-wrapper ' + props.className + ' ' + props.validityClass}
        style={{ width: props.width }}
      >
        <textarea
          id={`field-${props.fieldId}`}
          rows={props.rows}
          onChange={this.setFieldValue}
          onBlur={this.triggerBlur}
          onFocus={this.props.onFocus}
          name={props.name}
          autoComplete={props.autoComplete}
          disabled={props.disabled}
          data-index={props.index}
          value={props.value}
          placeholder={props.placeholder}
          // required={props.required}
          aria-describedby={
            props.fieldId && props.description
              ? `field-description-${props.fieldId} field-error-${props.fieldId}`
              : null
          }
          aria-required={props.required}
          required={props.required}
          aria-invalid={props.isValid === false}
          ref={(input) => {
            this.input = input
          }}
        />
      </div>
    )
  }
}

export class Select extends Component {
  constructor(props) {
    super(props)
    this.id = props.fieldId || shortid.generate()
  }
  setFieldValue = (e) => {
    let selectedIndex = this.props.options.findIndex((option) => option.value === e.target.value)
    this.props.onChange(e.target.value, e, { selectedIndex })
  }
  triggerBlur = (e) => {
    if (this.props.onBlur) {
      if (this.props.bubbleEvents) {
        this.props.onBlur(e)
      } else {
        this.props.onBlur(e.target.value)
      }
    }
  }
  focus = () => {
    this.input.focus()
  }
  getOptionStructure = () => {
    const { options, optionGroups } = this.props
    let groupsObj = {}
    let structure = []

    options.forEach((opt, i) => {
      const isInOptGroup = optionGroups.reduce((bool, optg) => {
        if (i >= optg.start && i <= optg.end) {
          return true
        }
        return bool
      }, false)
      if (isInOptGroup) {
        const group = optionGroups.find((optg) => i >= optg.start && i <= optg.end)
        groupsObj = {
          ...groupsObj,
          [group.id]: {
            ...group,
            ...groupsObj[group.id],
            options: [...((groupsObj[group.id] || {}).options || {}), { ...opt }],
          },
        }
        if (!structure.find((x) => x.type === 'group' && x.id === group.id)) {
          structure.push({ type: 'group', id: group.id })
        }
      } else {
        structure.push({ ...opt, type: 'option' })
      }
    })
    const combinedStrucuture = structure.map((x) => {
      if (x.type === 'option') {
        return x
      } else {
        return { ...x, ...groupsObj[x.id] }
      }
    })
    return combinedStrucuture
  }
  render() {
    let {
      className = '',
      validityClass = '',
      name,
      value = '',
      width,
      options = [],
      optionGroups,
      disabled,
      fieldId,
      required,
      ariaLabel,
      tabIndex,
      isValid,
    } = this.props
    return (
      <select
        className={`text-wrapper ${className} ${validityClass}`}
        value={value ? value.toString() : value}
        disabled={disabled}
        aria-required={required}
        required={required}
        aria-label={ariaLabel}
        aria-describedby={
          fieldId && this.props.description
            ? `field-description-${fieldId} field-error-${fieldId}`
            : null
        }
        aria-invalid={isValid === false}
        name={name}
        onChange={this.setFieldValue}
        onBlur={this.triggerBlur}
        style={{ width: width }}
        id={`field-${fieldId}`}
        tabIndex={tabIndex}
        ref={(input) => {
          this.input = input
        }}
      >
        {optionGroups
          ? this.getOptionStructure().map((x, i) => {
            if (x.type === 'option') {
              return (
                <option value={x.value.toString()} disabled={x.disabled} key={i}>
                  {x.label}
                </option>
              )
            } else {
              return (
                <optgroup label={x.label} key={i}>
                  {x.options.map((opt, k) => (
                    <option value={opt.value.toString()} key={k}>
                      {opt.label}
                    </option>
                  ))}
                </optgroup>
              )
            }
          })
          : options.map((option, i) => {
            return (
              <option disabled={option.disabled} value={option.value.toString()} key={i}>
                {option.label}
              </option>
            )
          })}
      </select>
    )
  }
}

export class RadioInput extends Component {
  constructor(props) {
    super(props)
    this.id = props.fieldId || shortid.generate()
  }
  focus = () => {
    this.radio.focus()
  }
  setFieldValue = (e) => {
    let selectedIndex = this.props.options.findIndex((option) => option.value === e)
    this.props.onChange(e, { target: { value: e, name: this.props.name } }, { selectedIndex })
  }
  setRef = (radio) => {
    this.radio = radio
  }
  render() {
    let {
      value = '',
      disabled,
      description,
      options = [],
      name,
      leftAlign,
      fieldId,
      label,
      required,
      tabIndex,
      isValid,
      leaveValuesUnstringified,
      hideLabel,
      ownsLabel,
    } = this.props
    return (
      <fieldset aria-required={required}>
        <legend className={`field-label ${hideLabel || !ownsLabel ? 'visually-hidden' : ''}`}>
          {label}
          {label && required && (
            <span
              className={`required ${hideLabel || !ownsLabel ? 'visually-hidden' : ''}`}
              aria-hidden="true"
            >
              *
            </span>
          )}
        </legend>
        {description !== '' ? (
          <ReactMarkdown id={fieldId ? `field-description-${fieldId}` : undefined} className="description" source={description} escapeHtml />
        ) : null}
        <RadioGroup
          id={`field-${fieldId}`}
          name={name}
          selectedValue={value.toString()}
          className={'radio-group ' + (disabled ? 'disabled' : '')}
          onChange={this.setFieldValue}
          tabIndex={tabIndex}
          aria-describedby={
            fieldId && this.props.description
              ? `field-description-${fieldId} field-error-${fieldId}`
              : null
          }
          aria-invalid={isValid === false}
        >
          {options.map((option, i) => {
            return (
              <div className={'option-wrapper ' + (leftAlign ? 'left-align' : '')} key={i}>
                <Radio
                  innerRef={(ref) => {
                    if (i === 0) {
                      this.setRef(ref)
                    }
                  }}
                  name={this.id}
                  id={this.id + i}
                  value={leaveValuesUnstringified === true ? option.value : option.value.toString()}
                  disabled={disabled}
                  tabIndex={tabIndex}
                  key={i}
                />
                <label className="field-label radio-label" htmlFor={this.id + i}>
                  {option.label}{' '}
                  {option.tooltip ? (
                    <span aria-hidden="true" className="radio-option-tip" title={option.tooltip}>
                      i
                    </span>
                  ) : (
                    ''
                  )}
                </label>
              </div>
            )
          })}
        </RadioGroup>
      </fieldset>
    )
  }
}

export class Checkbox extends Component {
  setFieldValue = (e) => {
    this.props.onChange(e.target.checked, e)
  }
  focus = () => {
    this.check.focus()
  }
  render() {
    let {
      name,
      value,
      label = '',
      required,
      disabled,
      tooltip,
      fieldId = '',
      tabIndex,
      isValid,
    } = this.props
    return (
      <div className="checkbox-wrapper">
        <input
          id={fieldId ? `field-${fieldId}` : undefined}
          name={name}
          type="checkbox"
          disabled={disabled}
          onChange={this.setFieldValue}
          checked={value || false}
          aria-label={label ? label : undefined}
          aria-required={required}
          required={required}
          aria-describedby={
            fieldId && this.props.description
              ? `field-description-${fieldId} field-error-${fieldId}`
              : null
          }
          aria-invalid={isValid === false}
          tabIndex={tabIndex}
          ref={(check) => {
            this.check = check
          }}
        // aria-labelledby={fieldId ? `label-${fieldId}` : undefined}
        />
        <label className="checkbox-box" htmlFor={fieldId ? `field-${fieldId}` : undefined}></label>
        <label
          className="checkbox-label"
          htmlFor={fieldId ? `field-${fieldId}` : undefined}
          id={fieldId ? `label-${fieldId}` : undefined}
        >
          {label}
          {required ? <span className="required">*</span> : null}
          {tooltip !== '' && tooltip ? <FieldTooltip tooltip={tooltip} /> : null}
        </label>
      </div>
    )
  }
}

export class DateInput extends Component {
  state = {
    open: false,
    showError: false,
    value: '',
  }

  componentDidMount = () => {
    this.registerEscapeListener()
  }
  setFieldValue = (value = '', e) => {
    let v = value.replace(/\D/g, '')
    if (v.length >= 5) {
      v = `${v.slice(0, 2)}/${v.slice(2, 4)}/${v.slice(4)}`
    } else if (v.length >= 3) {
      v = `${v.slice(0, 2)}/${v.slice(2)}`
    }
    v = v.slice(0, 10)
    this.props.onChange(v, e)
  }

  pickDate = (value, _) => {
    this.closePicker()

    this.props.onChange(value, { target: { value, name: this.props.name } })

    if (this.props.onButtonClicked) {
      this.props.onButtonClicked(value)
    }
    this.setState({ showError: false })
  }

  isValidDate = this.state.value
    ? isValid(new Date(this.state.value)) && this.state.value.length === 10
      ? true
      : false
    : undefined

  handleBlur = (value, e) => {
    if ((!value && this.props.businessRules && this.props.businessRules.some((r) => r.fn === 'allowEmptyDate')) ||
      (value && value.length >= 10 && isValid(new Date(value)))) {
      this.setState({ showError: false })
      if (!value) {
        this.props.onChange(null, e)
      }
      if (this.props.onBlur) {
        this.props.onBlur(format(new Date(value), 'MM/dd/yyyy'), e)
      }
    } else {
      this.setState({ showError: !!value })
      if (
        !this.props.businessRules ||
        this.props.businessRules.some((r) => r.fn === 'dateIsValid')
      ) {
        this.props.onChange(null, e)
      }
      if (this.props.onBlur) {
        this.props.onBlur(null, e)
      }
    }
  }
  openPicker = () => {
    this.textInput.blur()
    this.setState({
      open: true,
    })
    if (this.props.onPickerOpened) this.props.onPickerOpened()
  }
  closePicker = () => {
    this.setState({
      open: false,
    })
    if (this.props.onPickerClosed) this.props.onPickerClosed()
    this.unregisterEscapeListener()
  }
  registerEscapeListener = () => {
    document.addEventListener('keydown', this.checkForCalendarEscape)
  }
  unregisterEscapeListener = () => {
    document.removeEventListener('keydown', this.checkForCalendarEscape)
  }
  setToday = () => {
    const today = format(new Date(), 'MM/dd/yyyy')
    this.props.onChange(today, { target: { value: today, name: this.props.name } })
    if (this.props.onButtonClicked) {
      this.props.onButtonClicked(today)
    }
    this.setState({ showError: false })
  }
  focusDatePicker = () => {
    const input = document.querySelector('.react-datepicker__input-container input')
    if (input) {
      input.focus()
    }
  }
  checkForCalendarEscape = (e) => {
    if (e.keyCode === 27) {
      this.closePicker()
    }
  }
  handleMonthChange = (date) => {
    this.datePicker.setPreSelection(date)
  }
  handleYearChange = (date) => {
    this.datePicker.setPreSelection(date)
  }
  focusPreSelection = () => {
    const preSelection = document.querySelector('.react-datepicker__day--keyboard-selected')
    if (preSelection) {
      preSelection.focus()
    }
  }
  getSanitizedDates = (value) => {
    let inputValue =
      value && isValid(new Date(value)) ? format(new Date(value), 'MM/dd/yyyy') : value
    let date = value && isValid(new Date(value)) ? new Date(value) : null
    return [date, inputValue]
  }
  render() {
    let {
      value = '',
      className = '',
      validityClass = '',
      disabled,
      minDate,
      maxDate,
      width,
      fieldId,
      required,
      todayButton,
      tabIndex,
      ariaLabel,
      buttonClassName,
      inputDisabled,
    } = this.props
    const stringValue = typeof value === 'string' ? value : ''
    const isFullDate = stringValue.length === 10
    const [_date, inputValue] = isFullDate ? this.getSanitizedDates(value) : ['', stringValue]
    const [sanitizedMinDate] = minDate
      ? minDate.length === 10
        ? this.getSanitizedDates(minDate)
        : [undefined]
      : ''
    const [sanitizedMaxDate] = maxDate
      ? maxDate.length === 10
        ? this.getSanitizedDates(maxDate)
        : [undefined]
      : ''

    return (
      <React.Fragment>
        <div className={`date-picker-component`} style={{ position: 'relative' }}>
          <DumbField
            hideLabel
            width={width}
            className={`no-padding date-picker-input ${validityClass}`}
            fieldId={`date-${fieldId}`}
          >
            <TextInput
              fieldId={`date-${fieldId}`}
              className={`date-input ${className}`}
              placeholder="MM/DD/YYYY"
              onChange={this.setFieldValue}
              value={inputValue}
              width="100%"
              onBlur={this.handleBlur}
              ref={(ref) => {
                this.textInput = ref
              }}
              disabled={disabled || inputDisabled}
              required={required}
              tabIndex={tabIndex}
              ariaLabel={ariaLabel}
              disableSelectionRestoration
            />
          </DumbField>
          {todayButton && (
            <button
              type="button"
              disabled={disabled}
              className={`btn btn-primary btn-raised picker-button text-button ${buttonClassName}`}
              onClick={this.setToday}
              tabIndex={tabIndex}
              aria-label={`Enter today's date for: ${ariaLabel || this.props.label}`}
              title={`Enter today's date for: ${ariaLabel || this.props.label}`}
            >
              Today
            </button>
          )}
          <button
            type="button"
            disabled={disabled}
            className={`btn btn-primary btn-raised picker-button ${buttonClassName}`}
            aria-label={`Open calendar widget for: ${ariaLabel || this.props.label}`}
            title={`Open calendar widget for: ${ariaLabel || this.props.label}`}
            onClick={this.openPicker}
            tabIndex={tabIndex}
          >
            <CalendarIcon />
          </button>
          <Portal isOpened={this.state.open}>
            <div className="react-datepicker-portal" onKeyDown={this.checkForCalendarEscape}>
              <DatePicker
                isOpen={this.state.open}
                onChange={(val) => this.pickDate(format(new Date(val), 'MM/dd/yyyy'))}
                selected={
                  stringValue && isValid(new Date(stringValue)) ? new Date(stringValue) : null
                }
                selectedDate={
                  stringValue && isValid(new Date(stringValue)) ? new Date(stringValue) : null
                }
                calendarClassName="date-pop-in-down"
                disabled={disabled}
                placeholderText="MM/DD/YYYY"
                minDate={sanitizedMinDate}
                maxDate={sanitizedMaxDate}
                valid={this.isValidDate}
                onCalendarClose={this.closePicker}
              />
              <div
                className="shade animated fadeIn extra-fast-animation"
                onClick={this.closePicker}
              />
            </div>
          </Portal>
        </div>
        {(!this.props.businessRules ||
          this.props.businessRules.some((r) => r.fn === 'dateIsValid')) &&
          this.state.showError && (
            <p
              style={{ padding: '5px 0', fontStyle: 'italic', color: '#d62027', fontSize: '0.8em' }}
            >
              Date format must be MM/DD/YYYY
            </p>
          )}
      </React.Fragment>
    )
  }
}

export class ConfirmFields extends Component {
  constructor() {
    super()
    this.state = { masked: false }
  }
  focus = () => {
    this.firstInput.focus()
  }
  setFieldVal1 = (value, e) => {
    this.props.onChange({ val1: value, val2: this.props.value.val2 }, e)
  }
  setFieldVal2 = (value, e) => {
    this.props.onChange({ val1: this.props.value.val1, val2: value }, e)
  }
  handleFirstFocus = (e) => {
    this.setState({ masked: false })
  }
  handleFirstBlur = (e) => {
    this.props.onBlur({ ...this.props.value, val1: this.props.value.val1.trim() }, e, null, true, {
      subField: 'first',
    })
  }
  handleSecondFocus = (e) => {
    this.setState({ masked: true })
  }
  handleSecondBlur = (e) => {
    this.setState({ masked: false })
    this.props.onBlur(
      {
        val1: this.props.value.val1.trim(),
        val2: this.props.value.val2.trim(),
      },
      e,
      null,
      true,
      { subField: 'second' }
    )
  }
  getMasked = (value) => {
    let vals = value.split(' ')
    for (let i = 0; i < vals.length; i++) {
      vals[i] = vals[i].replace(/./g, '•')
    }
    return vals.join(' ')
  }
  render() {
    const {
      value,
      name,
      required,
      label,
      description,
      tooltip,
      disabled,
      placeholder,
      width,
      isValid,
      confirmFieldLabel,
      fieldId,
      match = null,
      maskTopField,
    } = this.props
    return (
      <div>
        <DumbField
          type="text"
          required={required}
          label={label}
          description={description}
          tooltip={tooltip}
          errorMessage=""
          hideLabel={false}
          fieldId={`field1-${fieldId}`}
        >
          <TextInput
            fieldId={`field1-${fieldId}`}
            value={
              maskTopField
                ? this.state.masked
                  ? this.getMasked(value.val1)
                  : value.val1
                : value.val1
            }
            onChange={this.setFieldVal1}
            onBlur={this.handleFirstBlur}
            onFocus={this.handleFirstFocus}
            name={name}
            disabled={disabled}
            placeholder={placeholder}
            required={required}
            width={width}
            validityClass={getValidityClass(isValid)}
            ref={(firstInput) => {
              this.firstInput = firstInput
            }}
          />
        </DumbField>
        <DumbField
          type="text"
          required={required}
          label={confirmFieldLabel}
          description=""
          errorMessage=""
          hideLabel={false}
          fieldId={`field2-${fieldId}`}
        >
          <TextInput
            fieldId={`field2-${fieldId}`}
            value={value.val2}
            onChange={this.setFieldVal2}
            onBlur={this.handleSecondBlur}
            onFocus={this.handleSecondFocus}
            name={name}
            disabled={disabled}
            placeholder={placeholder}
            required={required}
            width={width}
            validityClass={getValidityClass(isValid === false ? false : match)}
          />
        </DumbField>
      </div>
    )
  }
}

export class FullName extends Component {
  onChange = (value, e) => {
    this.props.onChange({ ...this.props.value, [e.target.name]: value }, e)
  }
  onBlur = (value, e) => {
    this.props.onChange({ ...this.props.value, [e.target.name]: value.trim() }, e)
    if (['LAST_NAME', 'SUFFIX'].includes(e.target.name)) this.props.onBlur(this.props.value, e)
  }
  focus = () => {
    this.fName.focus()
  }
  render() {
    const {
      value,
      required,
      isValid,
      disabled,
      suffix,
      fieldId,
      onlyRequireLastName,
      classNameMap = {},
    } = this.props

    return (
      <div className="row">
        <div className={classNameMap.firstName || 'col-sm-5 col-md-4 col-lg-3'}>
          <DumbField
            label="First Name"
            required={required && !onlyRequireLastName}
            description=""
            fieldId={`firstname-${fieldId}`}
          >
            <TextInput
              autoComplete="given-name"
              fieldId={`firstname-${fieldId}`}
              value={value.FIRST_NAME}
              onChange={this.onChange}
              onBlur={this.onBlur}
              name={'FIRST_NAME'}
              required={required}
              disabled={disabled}
              width="100%"
              validityClass={
                isValid || !onlyRequireLastName
                  ? getValidityClass(isValid)
                  : onlyRequireLastName
                    ? getValidityClass(null)
                    : getValidityClass(isValid)
              }
              ref={(fName) => {
                this.fName = fName
              }}
            />
          </DumbField>
        </div>
        <div className={classNameMap.middleName || 'col-sm-4 col-md-3 col-lg-2'}>
          <DumbField label="Middle/Initial" description="" fieldId={`middle-${fieldId}`}>
            <TextInput
              autoComplete="additional-name"
              fieldId={`middle-${fieldId}`}
              value={value.MIDDLE_NAME}
              onChange={this.onChange}
              onBlur={this.onBlur}
              name="MIDDLE_NAME"
              disabled={disabled}
              width="100%"
              validityClass={isValid ? getValidityClass(isValid) : getValidityClass(null)}
            />
          </DumbField>
        </div>
        <div className={classNameMap.lastName || 'col-sm-5 col-md-4 col-lg-3'}>
          <DumbField
            label="Last Name"
            required={required}
            description=""
            fieldId={`lastname-${fieldId}`}
          >
            <TextInput
              autoComplete="family-name"
              fieldId={`lastname-${fieldId}`}
              value={value.LAST_NAME}
              onChange={this.onChange}
              onBlur={this.onBlur}
              name={'LAST_NAME'}
              required={required}
              disabled={disabled}
              width="100%"
              validityClass={getValidityClass(isValid)}
            />
          </DumbField>
        </div>
        {suffix && (
          <div className={classNameMap.suffix || 'col-sm-4 col-md-3 col-lg-2'}>
            <DumbField label="Suffix" description="" fieldId={`suffix-${fieldId}`}>
              <TextInput
                autoComplete="honorific-suffix"
                fieldId={`suffix-${fieldId}`}
                value={value.SUFFIX}
                onChange={this.onChange}
                onBlur={this.onBlur}
                name={'SUFFIX'}
                disabled={disabled}
                width="100%"
                validityClass={isValid ? getValidityClass(isValid) : getValidityClass(null)}
              />
            </DumbField>
          </div>
        )}
      </div>
    )
  }
}

export const FullNameDisplay = ({ value }) =>
  value.FIRST_NAME +
  (value.MIDDLE_NAME !== '' ? ' ' + value.MIDDLE_NAME + ' ' : ' ') +
  value.LAST_NAME +
  (value.SUFFIX ? ` ${value.SUFFIX}` : '')

export class NameOrg extends Component {
  setFieldValue = (value, e) => {
    var val = { ...this.props.value, [e.target.name]: value }
    this.props.onChange(val, { target: { name: this.props.name, value: val } })
  }
  onBlur = (value, e) => {
    this.props.onBlur(this.props.value, e)
  }
  setNameValue = (value, e) => {
    this.props.onChange(value, e)
  }
  focus = () => {
    this.orgCheck.focus()
  }
  render() {
    const { value, required, disabled, checkboxLabel, fieldId, tooltip } = this.props
    const isOrganization = value.ORG_YN === 'N' || value.ORG_YN === false ? false : true
    return (
      <div className="name-org-control">
        <Checkbox
          fieldId={fieldId}
          value={isOrganization}
          name="ORG_YN"
          disabled={disabled}
          label={!checkboxLabel ? 'This entity is an organization' : checkboxLabel}
          tooltip={tooltip}
          onChange={this.setFieldValue}
          onBlur={this.onBlur}
          ref={(orgCheck) => {
            this.orgCheck = orgCheck
          }}
        />
        {isOrganization ? (
          <DumbField
            label="Organization"
            required={required}
            description=""
            fieldId={`name-org-${fieldId}`}
          >
            <TextInput
              {...this.props}
              autoComplete="organization"
              fieldId={`name-org-${fieldId}`}
              value={value.ORG_NAME}
              name="ORG_NAME"
              onChange={this.setFieldValue}
              onBlur={this.onBlur}
            />
          </DumbField>
        ) : (
          <FullName {...this.props} onChange={this.setNameValue} onBlur={this.onBlur} />
        )}
      </div>
    )
  }
}
export const HOUR_OPTIONS = [
  { value: '', label: '--' },
  ...Array(12)
    .fill(null)
    .map((_, i) => ({ value: i + 1, label: i + 1 })),
]

export const MINUTE_OPTIONS = [
  { value: '', label: '--' },
  ...Array(60)
    .fill(null)
    .map((_, i) => {
      const time = i.toString().length > 1 ? i : `0${i}`
      return { value: time, label: time }
    }),
]

export class TimeInput extends Component {
  getPeriod = (v) => v.split(' ')[1]
  getHour = (v) => (v.search(':') >= 0 ? v.split(':')[0] : '')
  getMinutes = (v) => (v.split(':')[1] || ' ').split(' ')[0]
  onHourChange = (hour) => {
    const { value } = this.props
    const period = this.getPeriod(value)
    const minutes = this.getMinutes(value)
    this.props.onChange(`${hour}:${minutes} ${period}`)
  }
  onMinutesChange = (minutes) => {
    const { value } = this.props
    const period = this.getPeriod(value)
    const hour = this.getHour(value)
    this.props.onChange(`${hour}:${minutes} ${period}`)
  }
  onPeriodChange = (value, splitValue, e) => {
    this.props.onChange(`${splitValue[0]} ${value}`, e)
  }
  onBlur = (splitValue, e) => {
    this.props.onBlur(this.props.value, e, undefined, true)
  }
  render() {
    const { fieldId, value, disabled, isValid } = this.props
    const splitValue = value.split(' ')
    return (
      <div className="flex-row time-input">
        <DumbField fieldId={`time-${fieldId}`}>
          <Select
            ariaLabel="Hour"
            disabled={disabled}
            width="60px"
            fieldId={`time-hours-${fieldId}`}
            value={this.getHour(value)}
            options={HOUR_OPTIONS}
            validityClass={getValidityClass(isValid)}
            onChange={(v, e) => {
              this.onHourChange(v)
            }}
            onBlur={(v, e) => {
              this.onBlur(splitValue, e)
            }}
          />
        </DumbField>
        <div className="colon"> : </div>
        <DumbField fieldId={`time-${fieldId}`}>
          <Select
            ariaLabel="Hour"
            disabled={disabled}
            width="60px"
            fieldId={`time-minutes-${fieldId}`}
            value={this.getMinutes(value)}
            options={MINUTE_OPTIONS}
            validityClass={getValidityClass(isValid)}
            onChange={(v, e) => {
              this.onMinutesChange(v)
            }}
            onBlur={(v, e) => {
              this.onBlur(splitValue, e)
            }}
          />
        </DumbField>
        <DumbField className="period-select" fieldId={`period-${fieldId}`}>
          <Select
            ariaLabel="Time period"
            disabled={disabled}
            fieldId={`period-${fieldId}`}
            value={splitValue[1]}
            validityClass={getValidityClass(isValid)}
            options={[
              { value: 'am', label: 'am' },
              { value: 'pm', label: 'pm' },
            ]}
            onChange={(v, e) => {
              this.onPeriodChange(v, splitValue)
            }}
            onBlur={(v, e) => {
              this.onBlur(splitValue, e)
            }}
          />
        </DumbField>
      </div>
    )
  }
}
